import {
  CButton,
  CCol,
  CInput,
  CLabel,
  CRow,
  CTextarea,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import { InvoiceItem } from "../../models/invoice";
import CurrencyField from "../currencies/CurrencyField";
import { FieldErrors } from "../form/FieldErrors";
import { emptyValueOnUndefined } from "../../utils/fields";
import { AutoInvoiceItem } from "../../models/auto-invoice";

const addTotalAmount = (
  item: AutoInvoiceItem,
  unitAmount?: number,
  quantity?: number
): InvoiceItem => {
  if (unitAmount !== undefined && quantity !== undefined) {
    item.totalAmount = unitAmount * quantity;
  }

  return item;
};

const addMissingValues = (item: InvoiceItem) => {
  const newItem = { ...item };
  // TODO: see how to handle this
  newItem.measureUnit = 77;

  return newItem;
};

interface AutoInvoiceItemFormProps {
  item: AutoInvoiceItem;
  currency: string;
  onDelete: (_: InvoiceItem) => void;
  onChange: (_: InvoiceItem) => void;
  errors: Errors;
}

const AutoInvoiceItemForm: React.FC<AutoInvoiceItemFormProps> = ({
  item,
  currency,
  onDelete,
  onChange,
  errors,
}) => {
  const [editingItem, setEditingItem] = useState(item);
  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const onQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };
      if (!isNaN(parseFloat(e.target.value))) {
        newItem.quantity = parseFloat(e.target.value);
        newItem = addTotalAmount(
          newItem,
          newItem.unitAmount,
          parseFloat(e.target.value)
        );
      } else {
        newItem.quantity = undefined;
        newItem = addTotalAmount(newItem, newItem.unitAmount, undefined);
      }

      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.code = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.description = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onUnitAmountChange = useCallback(
    (newUnitAmount?: number) => {
      let newItem = { ...editingItem };
      newItem.unitAmount = newUnitAmount;
      newItem = addTotalAmount(newItem, newUnitAmount, newItem.quantity);
      newItem = addMissingValues(newItem);
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDiscountChange = useCallback(
    (newDiscount?: number) => {
      let newItem = { ...editingItem };
      newItem.discount = newDiscount;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  useEffect(() => {
    setEditingItem(item);
  }, [item]);

  const itemLabelStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "var(--theme-muted)",
    marginBottom: "3px",
    display: "block",
  };

  return (
    <div
      style={{
        position: "relative",
        border: "1px solid var(--theme-border)",
        borderLeft: "4px solid #006657",
        borderRadius: "6px",
        marginBottom: "10px",
        padding: "12px 14px 10px",
        background: "var(--theme-surface)",
      }}
    >
      {/* Botón eliminar: siempre arriba a la derecha */}
      <div style={{ position: "absolute", top: "10px", right: "12px" }}>
        <CButton
          color="danger"
          variant="outline"
          size="sm"
          onClick={onDeleteClick}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </div>

      {/* Fila 1: Cantidad | Código | Precio Unitario | Descuento */}
      <CRow className="align-items-end mb-2" style={{ paddingRight: "46px" }}>
        <CCol xs={4} md={1}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Cant.
          </CLabel>
          <CInput
            type="number"
            step="0.001"
            value={emptyValueOnUndefined(editingItem.quantity)}
            onChange={onQuantityChange}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("quantity", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={8} md={2}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Código
          </CLabel>
          <CInput
            type="text"
            value={emptyValueOnUndefined(editingItem.code)}
            onChange={onCodeChange}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("code", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={6} md={3}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Precio Unitario
          </CLabel>
          <CurrencyField
            currency={currency}
            placeholder="Monto"
            onChange={onUnitAmountChange}
            value={editingItem.unitAmount}
            limit={1000000000000}
          />
          <FieldErrors
            errors={getFieldErrors("unitAmount", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={6} md={3}>
          <CLabel style={itemLabelStyle}>Descuento</CLabel>
          <CurrencyField
            currency={currency}
            placeholder="Monto"
            onChange={onDiscountChange}
            value={editingItem.discount}
            limit={1000000000000}
          />
          <FieldErrors
            errors={getFieldErrors("discount", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* Fila 2: Detalle */}
      <CRow>
        <CCol>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Detalle
          </CLabel>
          <CTextarea
            placeholder="Detalle del ítem"
            value={emptyValueOnUndefined(editingItem.description)}
            onChange={onDescriptionChange}
            style={{ resize: "vertical", minHeight: "60px" }}
          ></CTextarea>
          <FieldErrors
            errors={getFieldErrors("description", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>
    </div>
  );
};

const propsAreEqual = (
  prevItemProps: AutoInvoiceItemFormProps,
  nextItemProps: AutoInvoiceItemFormProps
): boolean => {
  return (
    prevItemProps.item.id === nextItemProps.item.id &&
    prevItemProps.item.obs === nextItemProps.item.obs &&
    prevItemProps.item.measureUnit === nextItemProps.item.measureUnit &&
    prevItemProps.item.unitAmount === nextItemProps.item.unitAmount &&
    prevItemProps.item.description === nextItemProps.item.description &&
    prevItemProps.item.quantity === nextItemProps.item.quantity &&
    prevItemProps.item.totalAmount === nextItemProps.item.totalAmount &&
    errorsAreSame(prevItemProps.errors, nextItemProps.errors)
  );
};

export default React.memo(AutoInvoiceItemForm, propsAreEqual);
